import * as styles from "./SelectTrigger.css";

import { clsx } from "clsx";
import { useLayoutEffect, useRef } from "react";

import { Select as BaseSelect } from "@base-ui/react/select";
import { Icon } from "@frontend/components/atoms/Icon";

import { useSelectContext } from "./Select.context";
import type { SelectTriggerProps } from "./Select.types";

export function SelectTrigger({
  className,
  children,
  variant,
  invalid,
  leftSection,
  rightSection,
  hideIcon,
  ...props
}: SelectTriggerProps) {
  const { size, layout, onLeftInsetChange, clearable, hasValue, clear } =
    useSelectContext();

  const isText = layout === "text";

  const leftSectionRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = leftSectionRef.current;

    if (!element) {
      onLeftInsetChange(0);

      return;
    }

    const trigger = element.parentElement;

    const measure = () => {
      const gap = trigger
        ? parseFloat(getComputedStyle(trigger).columnGap) || 0
        : 0;

      onLeftInsetChange(element.offsetWidth + gap);
    };

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [leftSection, onLeftInsetChange]);

  const showClear = clearable && hasValue;

  return (
    <BaseSelect.Trigger
      data-slot="select-trigger"
      className={clsx(
        isText
          ? styles.textTrigger({ invalid })
          : styles.select({ size, variant, invalid }),
        className,
      )}
      onKeyDown={(event) => {
        if (
          showClear &&
          (event.key === "Delete" || event.key === "Backspace")
        ) {
          event.preventDefault();
          clear();
        }

        props.onKeyDown?.(event);
      }}
      {...props}
    >
      {leftSection ? (
        <span ref={leftSectionRef} className={styles.leftSection}>
          {leftSection}
        </span>
      ) : null}

      {children}

      {rightSection ? (
        <span className={styles.rightSection}>{rightSection}</span>
      ) : null}

      {showClear ? (
        <span
          data-slot="select-clear"
          className={styles.clear}
          aria-hidden="true"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            clear();
          }}
        >
          <Icon name="X" size="sm" />
        </span>
      ) : null}

      {hideIcon ? null : (
        <BaseSelect.Icon className={isText ? styles.textIcon : styles.icon}>
          <Icon name="ChevronDown" />
        </BaseSelect.Icon>
      )}
    </BaseSelect.Trigger>
  );
}
